// frontend/src/components/calendar/workingDayResolver.js
//
// Client-side mirror of backend api.services.calendar_resolver.
// Given a calendar's working-days pattern + the holidays list + the
// exceptions list, returns the resolved kind of any date with the
// same precedence the backend applies:
//
//   1. ExceptionDate match  → 'exception'
//   2. (Month, Day) match   → 'holiday'
//   3. pattern[weekday]     → 'working' (if true) or 'weekend' (if false)
//
// The two pieces (backend + frontend) MUST stay in sync. They're both
// small enough that duplicating the rule is cheaper than a per-month
// network call.

export const WEEKDAY_KEYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
];

/**
 * @param {{working_days_pattern?: object}} calendar
 * @param {{month:number, day:number, holiday_name?:string}[]} holidays
 * @param {{exception_date:string, exception_name?:string}[]} exceptions
 * @returns {{
 *   resolve(yyyyMmDd:string): {kind:'working'|'weekend'|'holiday'|'exception', label:string},
 *   findHoliday(month:number, day:number): object|null,
 *   findException(yyyyMmDd:string): object|null,
 * }}
 */
export function buildResolver(calendar, holidays, exceptions) {
  const pattern = calendar?.working_days_pattern || {};
  const patternByWeekday = WEEKDAY_KEYS.map(k => !!pattern[k]);

  const holidayByMd = new Map();
  for (const h of (holidays || [])) {
    holidayByMd.set(monthDayKey(h.month, h.day), h);
  }

  const exceptionByDate = new Map();
  for (const e of (exceptions || [])) {
    exceptionByDate.set(e.exception_date, e);
  }

  function resolve(yyyyMmDd) {
    const exc = exceptionByDate.get(yyyyMmDd);
    if (exc) return { kind: 'exception', label: exc.exception_name || '' };

    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    const hol = holidayByMd.get(monthDayKey(m, d));
    if (hol) return { kind: 'holiday', label: hol.holiday_name || '' };

    // JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat. Convert to Mon=0..Sun=6.
    const jsDow = new Date(y, m - 1, d).getDay();
    const monFirstIdx = (jsDow + 6) % 7;
    return {
      kind: patternByWeekday[monFirstIdx] ? 'working' : 'weekend',
      label: '',
    };
  }

  function findHoliday(month, day) {
    return holidayByMd.get(monthDayKey(month, day)) || null;
  }

  function findException(yyyyMmDd) {
    return exceptionByDate.get(yyyyMmDd) || null;
  }

  return { resolve, findHoliday, findException };
}

function monthDayKey(month, day) {
  return `${Number(month)}-${Number(day)}`;
}

/**
 * Format month names for navigation labels using the user's locale.
 */
export function formatMonthLabel(year, month, locale) {
  return new Date(year, month - 1, 1).toLocaleDateString(locale, {
    month: 'long', year: 'numeric',
  });
}

/**
 * Short weekday names in the user's locale, one per WEEKDAY_KEYS entry
 * (Monday first).
 */
export function formatWeekdayShortLabels(locale) {
  // 2024-01-01 was a Monday.
  return WEEKDAY_KEYS.map((_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(locale, {
    weekday: 'short',
  }));
}

/**
 * Format a holiday's (month, day) as a date label in the user's locale,
 * with the year intentionally omitted (recurring).
 */
export function formatMonthDayLabel(month, day, locale) {
  return new Date(2001, month - 1, day).toLocaleDateString(locale, {
    day: '2-digit', month: 'short',
  });
}
